import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'

/**
 * Central registry + mapping store for the "any UI control can be
 * driven by any MIDI message" feature.
 *
 * Two kinds of state cohabit here:
 *   - **Persisted bindings:** `controlId → MidiBinding[]`. Survives
 *     reloads. Keyed by stable string ids the components choose
 *     (e.g. `transport.play`, `sequencer.bpm`, `pad.3.gain`). Re-mount
 *     of a component picks up its existing bindings automatically.
 *   - **Runtime registry:** `controlId → ControlDescriptor`. Not
 *     persisted; filled on mount by `useMidiMappable` and cleared
 *     on unmount. Holds the live callbacks + element-ref getter that
 *     the dispatch layer and Learn/Reveal overlays need.
 *
 * The split lets a control remember its bindings even when the
 * component isn't mounted — when the user reopens a panel, its
 * controls rebind silently.
 */

export type ControlKind = 'trigger' | 'toggle' | 'continuous' | 'discrete'

export interface ControlDescriptor {
  /** Stable id, dot-segmented by convention: `section.control[.index]`. */
  id: string
  kind: ControlKind
  /** Display name shown in badges / Learn overlay / mapping list. */
  label: string
  /** Min/max for continuous controls; ignored for other kinds. */
  min?: number
  max?: number
  /** Ordered enum values for discrete controls. */
  values?: readonly string[]
  /** Set by continuous/toggle/discrete controls. */
  setValue?: (v: number | boolean | string) => void
  /** Fired by trigger controls (Play, pad hit, one-shot buttons). */
  trigger?: () => void
  /** Returns the current DOM element for positioning badges/rings. */
  getElement?: () => HTMLElement | null
}

export type MidiBindingKind = 'note' | 'cc' | 'pitchbend' | 'programchange'

export interface MidiBinding {
  /** Binding id — uuid-ish, used as a list key + for remove actions. */
  id: string
  kind: MidiBindingKind
  /** 0 = omni (any channel), otherwise 1..16. */
  channel: number
  /** Note #, CC #, or program #. Ignored when `kind === 'pitchbend'`. */
  number: number
  /** Optional — restrict to a specific MIDI input device by id. */
  deviceId?: string
  /** Output scale for continuous controls. `undefined` → control's
   *  own [min,max]. Lets the user dedicate a narrower range of a
   *  knob to a specific control. */
  scaleMin?: number
  scaleMax?: number
  /** Curve for continuous mapping. Linear by default. */
  curve?: 'linear' | 'log' | 'exp'
  /** Absolute = knob position IS the value. Relative = endless-encoder
   *  style where values 65..127 mean +1,+2,… and 63..1 mean -1,-2,…
   *  Toggle = any incoming value > 0 flips a boolean. Default differs
   *  by ControlKind (see GlobalMidiProvider for resolution). */
  behavior?: 'absolute' | 'relative' | 'toggle'
}

interface MidiMappingState {
  bindings: Record<string, MidiBinding[]>
  /** Runtime-only. Excluded from persistence. */
  descriptors: Record<string, ControlDescriptor>
  learnMode: boolean
  learnTargetId: string | null

  registerControl: (descriptor: ControlDescriptor) => void
  unregisterControl: (id: string) => void

  addBinding: (controlId: string, binding: Omit<MidiBinding, 'id'>) => string
  removeBinding: (controlId: string, bindingId: string) => void
  clearBindingsFor: (controlId: string) => void
  clearAllBindings: () => void

  setLearnMode: (on: boolean) => void
  setLearnTarget: (id: string | null) => void
}

/** tiny uuid-ish — good enough for list keys, no external dep. */
function rid(): string {
  return (
    Math.random().toString(36).slice(2, 10) +
    Math.random().toString(36).slice(2, 10)
  )
}

export const useMidiMappingStore = create<MidiMappingState>()(
  persist(
    (set, get) => ({
      bindings: {},
      descriptors: {},
      learnMode: false,
      learnTargetId: null,

      registerControl: descriptor =>
        set(state => ({
          descriptors: { ...state.descriptors, [descriptor.id]: descriptor },
        })),

      unregisterControl: id =>
        set(state => {
          if (!(id in state.descriptors)) return state
          const next = { ...state.descriptors }
          delete next[id]
          const patch: Partial<MidiMappingState> = { descriptors: next }
          // If the removed control was the current learn target, drop
          // the target so the next incoming message doesn't bind to a
          // now-unmounted control.
          if (state.learnTargetId === id) patch.learnTargetId = null
          return patch as MidiMappingState
        }),

      addBinding: (controlId, binding) => {
        const id = rid()
        set(state => {
          const existing = state.bindings[controlId] ?? []
          return {
            bindings: {
              ...state.bindings,
              [controlId]: [...existing, { ...binding, id }],
            },
          }
        })
        return id
      },

      removeBinding: (controlId, bindingId) =>
        set(state => {
          const existing = state.bindings[controlId]
          if (!existing) return state
          const filtered = existing.filter(b => b.id !== bindingId)
          const next = { ...state.bindings }
          if (filtered.length === 0) delete next[controlId]
          else next[controlId] = filtered
          return { bindings: next }
        }),

      clearBindingsFor: controlId =>
        set(state => {
          if (!(controlId in state.bindings)) return state
          const next = { ...state.bindings }
          delete next[controlId]
          return { bindings: next }
        }),

      clearAllBindings: () => set({ bindings: {} }),

      setLearnMode: on =>
        set(state => ({
          learnMode: on,
          // Drop any pending learn target when toggling off so a
          // re-enable starts fresh instead of auto-capturing to a
          // stale arm.
          learnTargetId: on ? state.learnTargetId : null,
        })),

      setLearnTarget: id => {
        // No-op if not in learn mode — guards against accidental
        // arm calls when learn was toggled off mid-click.
        if (!get().learnMode && id !== null) return
        set({ learnTargetId: id })
      },
    }),
    {
      name: 'busswerks-midi-mappings',
      // Only bindings persist. Runtime-only fields (descriptors,
      // learn state) are re-derived on mount.
      partialize: state => ({ bindings: state.bindings }),
      storage: createJSONStorage(() =>
        typeof window === 'undefined' ? memoryStorage() : window.localStorage,
      ),
    },
  ),
)

/** Selectors — keep them outside the create call so memoization is
 *  reliable and components can use them with `useMidiMappingStore(selectX)`. */
export const selectBindingsFor =
  (controlId: string) =>
  (state: MidiMappingState): MidiBinding[] =>
    state.bindings[controlId] ?? EMPTY_BINDINGS

/** Stable empty array so selectors return the same reference when a
 *  control has no bindings — avoids bogus re-renders. */
const EMPTY_BINDINGS: readonly MidiBinding[] = Object.freeze([])

/** Match an incoming message's (kind, channel, number) against a
 *  binding. Channel `0` matches any channel. Pitchbend ignores
 *  `number`. Used by GlobalMidiProvider during dispatch. */
export function bindingMatches(
  binding: MidiBinding,
  kind: MidiBindingKind,
  channel: number,
  number: number,
  deviceId: string,
): boolean {
  if (binding.kind !== kind) return false
  if (binding.channel !== 0 && binding.channel !== channel) return false
  if (binding.deviceId && binding.deviceId !== deviceId) return false
  if (kind === 'pitchbend') return true
  return binding.number === number
}

/** Build a short human label for a binding — used in badges + the
 *  mapping list. Examples: `"Note C3 ch1"`, `"CC 7 ch1"`,
 *  `"Pitch ch1"`, `"Prog 4 ch1"`. Keeps the device id off the label
 *  for brevity — the mapping list row shows the device separately. */
export function formatBindingLabel(binding: MidiBinding): string {
  const chan = binding.channel === 0 ? 'omni' : `ch${binding.channel}`
  switch (binding.kind) {
    case 'note':
      return `Note ${noteShort(binding.number)} ${chan}`
    case 'cc':
      return `CC ${binding.number} ${chan}`
    case 'pitchbend':
      return `Pitch ${chan}`
    case 'programchange':
      return `Prog ${binding.number} ${chan}`
  }
}

const NOTE_NAMES = [
  'C',
  'C#',
  'D',
  'D#',
  'E',
  'F',
  'F#',
  'G',
  'G#',
  'A',
  'A#',
  'B',
] as const
function noteShort(n: number): string {
  return `${NOTE_NAMES[n % 12]}${Math.floor(n / 12) - 1}`
}

function memoryStorage(): Storage {
  const store = new Map<string, string>()
  return {
    get length() {
      return store.size
    },
    clear: () => store.clear(),
    getItem: key => store.get(key) ?? null,
    key: index => Array.from(store.keys())[index] ?? null,
    removeItem: key => {
      store.delete(key)
    },
    setItem: (key, value) => {
      store.set(key, value)
    },
  }
}
